import React, { useState, useEffect, useMemo, useRef } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import PaginationComponent from "../pagination/pagination";
import axiosInstance from "../axios-instance.js";
import { useNavigate } from "react-router-dom";

const TabsComponent = ({ parameters, myDHLCtryCd, myDHLCtryLocale, client, source, isDynamic  }) => {
  const [url, setUrl] = useState("");
  const srefArray = useMemo(
    () => parameters.sref.split(","),
    [parameters.sref]
  );

  const [shipmentData, setShipmentData] = useState([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0); // Track the selected tab
  const [currentShipments, setCurrentShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0); // Track the pagination page
  const backendUrl = window.env.BACKEND_URL ? window.env.BACKEND_URL : window.location.origin;

  const pageSize = 10; // Number of items per page
  const cachedData = useMemo(() => Array(srefArray.length).fill(null), [srefArray.length]); // Cache for shipment data

  const navigate = useNavigate(); // Initialize useNavigate
  const iframeRef = useRef(null); // Ref for the iframe
  const observerRef = useRef(null);

  const handleNavigate = (event) => {
    event.preventDefault(); // Prevent the default anchor behavior
    if(source && source === 'home') {
      navigate(`/`); // Navigate to the dynamic route
    } else {
      navigate(`/${parameters.custcode}`);
    }
  };

  // Fetch shipment data
  const fetchShipments = async (sref) => {
    setLoading(true);
    setError(null); // Reset error state before API call

    try {
      const { locale, custcode, fromDate, toDate, origin, destination } = parameters;
      const requestBody = {
        custcode: custcode,
        srefs: [srefArray[currentTabIndex]], // Pass only the current sref as an array
        locale: locale || "en",
        fromDate: fromDate,
        toDate: toDate,
        origin: origin ? origin : "",
        destination: destination ? destination : ""
      };

      if(client) {
        const response = await axiosInstance.post(
          "/getShipmentDetails",
          requestBody
        );

        const data = response.data;
        cachedData[currentTabIndex] = data.shipments; // Store data in cache

        setShipmentData(data.shipments);

        // Set the first page shipments
        const initialShipments = data.shipments.slice(0, pageSize);
        setCurrentShipments(initialShipments);

        // Generate initial URL
        updateUrl(initialShipments);
      }
    } catch (err) {
      setError("No Status available");
    } finally {
      setLoading(false);
    }
  };

  // Function to update the URL based on shipments
  const updateUrl = (shipments) => {
    if (shipments.length > 0) {
      const shpkeys = shipments.map((shipment) => shipment.shpKey).join("&id=");
      const myDHLUrl = `${backendUrl}/reverse/${myDHLCtryCd.toLowerCase()}/${myDHLCtryLocale.toLowerCase()}/tracking.html?isDynamic=${isDynamic}&type=0#/results?id=${shpkeys}`;
      setUrl(myDHLUrl);

    } else {
      setUrl(""); // Clear URL if there are no shipments
    }

  };

  // Effect to load shipments for the selected tab
  useEffect(() => {
    setCurrentPage(0); // Reset to first page when changing tabs
    const sref = srefArray[currentTabIndex]; // Get SREF value from the array based on selected tab index

    if (cachedData[currentTabIndex]) {
      // If cached data exists, use it
      setShipmentData(cachedData[currentTabIndex]);
      const initialShipments = cachedData[currentTabIndex].slice(0, pageSize);
      setCurrentShipments(initialShipments);
      updateUrl(initialShipments); // Update URL with cached data
    } else {
      fetchShipments(sref); // Fetch new data
    }
  }, [currentTabIndex, srefArray]);

  // Handle page change (pagination)
  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected); // Update pagination state

    const startIndex = selectedPage.selected * pageSize;
    const newShipments = shipmentData.slice(startIndex, startIndex + pageSize);

    setCurrentShipments(newShipments);
    updateUrl(newShipments); // Update URL when page changes
  };

  // Function to truncate tab names
  const truncateTabName = (name) => {
    if (name.length > 10) {
      return { displayName: name.substring(0, 10) + '...', fullName: name };
    }
    return { displayName: name, fullName: name };
  };

    // Resize iframe based on content
  const resizeIframe = () => {
    if (iframeRef.current) {
      const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
  
      if (iframeDocument) {
          // Create a new MutationObserver instance
          const observer = new MutationObserver(() => {
            const newHeight = iframeDocument.body.scrollHeight + 32; // Add 32px to the height
            iframeRef.current.style.height = newHeight + 'px';
          });
  
          // Start observing the body for changes
          observer.observe(iframeDocument.body, {
            childList: true,
            subtree: true,
          });
  
          // Store the observer in the ref for cleanup
          observerRef.current = observer;

      } else {
        console.warn("Unable to access iframe document in sref result page.");
      }
    } else {
      console.warn("iframeRef.current is null in sref result page.");
    }
  };
  
  //Cleanup function to disconnect the observer when the component unmounts or iframe source changes
  useEffect(() => {
    return () => {
      cleanupObserver();
    };
  }, []);

  const cleanupObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  };

  return (
    <div>
      <Tabs
        selectedIndex={currentTabIndex}
        onSelect={(index) => {
          cleanupObserver();
          setCurrentTabIndex(index);
        }
        }
      >
        <TabList>
          {/* Dynamically generate Tab labels from srefArray */}
          {srefArray.map((sref, index) => {
            const { displayName, fullName } = truncateTabName(sref);
            return (
              <Tab key={index} title={fullName} style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {displayName}
              </Tab>
            );
          })}
        </TabList>

        {/* Tab Panels */}
        {srefArray.map((sref, index) => (
          <TabPanel key={index}>
            {/* Display loading or error message */}
            {loading && <p className="text-center">Loading shipments...</p>}
            {error && <p className="text-center" >{error}</p>}

            {/* Display the shipments inside an iframe */}
            {!loading && !error && (
              <>
                {currentShipments.length > 0 ? (
                  <iframe
                    ref={iframeRef}
                    title={`SREF ${sref} Shipments`}
                    src={url}
                    style={{ width: "100%", minHeight: "calc(100vh - 395px)", border: "none"}}
                    onLoad={resizeIframe} // Call resize function on load
                  ></iframe>
                ) : (
                  <p className="text-center">No Status available</p>
                )}
              </>
            )}
            <div className="row align-items-center">
            <div className="col-6">
              <button className="back-to-search-button text-02" onClick={handleNavigate} >
                Back to Search
              </button>
            </div>
              <div className="col-6 text-right">
                <PaginationComponent
                  onPageChange={handlePageChange}
                  totalItems={shipmentData.length}
                  pageSize={pageSize}
                />
              </div>
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

export default TabsComponent;
